/* OpenAI transcription lane for tools that otherwise transcribe in the browser
   (/auto-captions, /lyrics-transcription). One door: the same server endpoint,
   billing and job as /audio-to-text's «OpenAI» card (AudioToTextHandler::upload,
   TranscribeAudioJob → App\Services\Voice\OpenAiAsr). PAID on every tier, intl
   only — the page gets `window.__openAiAsr` from partials.openai-asr-lane only
   where the model exists.

   window.OpenAiAsrLane.available()            → is the choice offered here
   window.OpenAiAsrLane.costFor(seconds)       → tokens this file will hold
   window.OpenAiAsrLane.transcribe(pcm, rate, opts) → Promise<{segments, language}>
       pcm  Float32Array, mono; rate its sample rate (resampled to 16 kHz here)
       opts {lang, content: 'speech'|'music', prompt, signal, onProgress(frac)}
       segments [{start, end, text}] — seconds; each is one request's piece
   Refusals reject with an Error whose `.code` the page turns into its words:
       login · tokens (need, have, short) · locked · busy · lang · unavailable ·
       running (another transcription of theirs is going) · too_large ·
       throttled · failed (refunded: true when the job failed after the hold)
   Measured choice and price: docs/why/asr-openai.md. */
(function () {
    'use strict';
    var CFG = window.__openAiAsr || null;

    function err(code, extra) {
        var e = new Error('openai_asr_' + code);
        e.code = code;
        if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) e[k] = extra[k];
        return e;
    }

    /* Linear resample to 16 kHz — the server decodes to 16 kHz mono anyway, and a
       16-kHz upload is a third of a 48-kHz one. */
    function to16k(pcm, rate) {
        if (!rate || rate === 16000) return pcm;
        var ratio = rate / 16000, n = Math.floor(pcm.length / ratio), out = new Float32Array(n);
        for (var i = 0; i < n; i++) {
            var x = i * ratio, i0 = Math.floor(x), f = x - i0;
            var a = pcm[i0] || 0, b = pcm[i0 + 1] !== undefined ? pcm[i0 + 1] : a;
            out[i] = a + (b - a) * f;
        }
        return out;
    }

    function wavBlob(pcm16k) {
        var n = pcm16k.length, buf = new ArrayBuffer(44 + n * 2), v = new DataView(buf);
        function str(o, s) { for (var i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); }
        str(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); str(8, 'WAVE');
        str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
        v.setUint32(24, 16000, true); v.setUint32(28, 32000, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
        str(36, 'data'); v.setUint32(40, n * 2, true);
        for (var i = 0, o = 44; i < n; i++, o += 2) {
            var s = Math.max(-1, Math.min(1, pcm16k[i]));
            v.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        }
        return new Blob([buf], { type: 'audio/wav' });
    }

    function csrf() {
        var m = document.querySelector('meta[name="csrf-token"]');
        return m ? m.content : '';
    }

    function sleep(ms, signal) {
        return new Promise(function (res, rej) {
            var t = setTimeout(res, ms);
            if (signal) signal.addEventListener('abort', function () { clearTimeout(t); rej(err('cancelled')); }, { once: true });
        });
    }

    function refusal(status, d) {
        d = d || {};
        if (status === 401 || d.error === 'login_required') return err('login');
        if (status === 402 || d.error === 'quota_exceeded') {
            if (d.obstacle === 'locked') return err('locked');
            if (d.obstacle === 'day_site') return err('busy');
            return err('tokens', { need: d.tokens_needed || 0, have: d.tokens_have || 0, short: d.tokens_short || 0 });
        }
        if (d.error === 'model_lang' || d.error === 'model_no_translate') return err('lang');
        if (d.error === 'model_unavailable') return err('unavailable');
        if (status === 413 || d.error === 'too_large') return err('too_large');
        if (status === 429) return err('throttled');
        return err('failed', { detail: d.error || ('http_' + status) });
    }

    function poll(jobId, signal, onProgress) {
        var url = CFG.statusUrl.replace(/\/0$/, '/' + jobId);
        var delay = 1500;
        function once() {
            return fetch(url, { headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, signal: signal, credentials: 'same-origin' })
                .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return [r.status, d]; }); })
                .then(function (sd) {
                    var d = sd[1];
                    if (d.status === 'done' && d.result) return d.result;
                    if (d.status === 'error') {
                        /* A failed OpenAI job settled as no charge: the tokens are back. */
                        throw err('failed', { detail: String(d.error || '').split(':')[0], refunded: true });
                    }
                    if (sd[0] === 404) throw err('failed', { detail: 'not_found' });
                    if (onProgress && d.progress) {
                        var m = /^(\d+)\/(\d+)$/.exec(String(d.progress));
                        if (m && +m[2] > 0) onProgress(+m[1] / +m[2]);
                    }
                    return sleep(delay, signal).then(function () { delay = Math.min(4000, delay + 500); return once(); });
                });
        }
        return once();
    }

    window.OpenAiAsrLane = {
        available: function () { return !!(CFG && CFG.uploadUrl && CFG.statusUrl); },
        loggedIn: function () { return !!(CFG && CFG.loggedIn); },
        langs: function () { return (CFG && CFG.langs) || []; },
        rate: function () { return (CFG && CFG.rate) || 0; },
        topupUrl: function () { return (CFG && CFG.topupUrl) || ''; },
        /* Same arithmetic as the server's hold: per STARTED minute, Premium −5 %. */
        costFor: function (seconds) {
            if (!CFG) return 0;
            var mins = Math.max(1, Math.ceil((seconds || 0) / 60));
            var mult = CFG.isPro ? (1 - (CFG.proDiscount || 0)) : 1;
            return Math.max(1, Math.ceil(mins * (CFG.rate || 0) * mult));
        },
        tokens: function () { return (CFG && CFG.tokens) | 0; },
        transcribe: function (pcm, rate, opts) {
            opts = opts || {};
            if (!this.available()) return Promise.reject(err('unavailable'));
            if (!CFG.loggedIn) return Promise.reject(err('login'));
            var fd = new FormData();
            fd.append('audio', wavBlob(to16k(pcm, rate)), 'audio.wav');
            fd.append('model', 'openai');
            fd.append('lang', opts.lang && opts.lang !== 'auto' ? String(opts.lang).split('-')[0] : 'auto');
            fd.append('content', opts.content === 'music' ? 'music' : 'speech');
            if (opts.prompt) fd.append('prompt', String(opts.prompt).slice(0, 600));
            var init = { method: 'POST', credentials: 'same-origin', signal: opts.signal,
                headers: { 'X-CSRF-TOKEN': csrf(), 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' } };
            var send = typeof window.__uploadFetch === 'function'
                ? window.__uploadFetch(CFG.uploadUrl, fd, init, { signal: opts.signal })
                : fetch(CFG.uploadUrl, Object.assign({ body: fd }, init));
            return send
                .then(function (r) { return r.json().catch(function () { return {}; }).then(function (d) { return [r.status, d]; }); })
                .then(function (sd) {
                    var d = sd[1];
                    if (!(sd[0] === 202 || sd[0] === 200) || !d.job_id) throw refusal(sd[0], d);
                    /* The lane runs one job per person: `reused` is the job they
                       already have going — another file. Its text must not land
                       here as this file's. */
                    if (d.reused) throw err('running');
                    if (typeof d.tokens_held === 'number' && CFG) CFG.tokens = Math.max(0, (CFG.tokens | 0) - d.tokens_held);
                    return poll(d.job_id, opts.signal, opts.onProgress);
                })
                .then(function (res) {
                    var segs = (res.segments || []).map(function (s, i, all) {
                        var start = +s.timestamp || 0;
                        var end = s.end != null ? +s.end : (all[i + 1] ? +all[i + 1].timestamp : start + 2);
                        return { start: start, end: Math.max(start + 0.2, end), text: String(s.text || '').trim() };
                    }).filter(function (s) { return s.text; });
                    return { segments: segs, language: res.detectedLang || null };
                });
        },
    };
})();
